import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Autocomplete, Button, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { getMasters, getStateCountries, type StateCountrySummary } from '../../services/platform/masterService';
import { countryFlag } from '../../utils/stateMasterValidation';

export type StateStatusFilter = 'all' | 'active' | 'inactive';

interface StateFilterOption { iso: string; code: string; name: string }

interface Props {
  country: string;
  status: StateStatusFilter;
  onCountryChange: (code: string) => void;
  onStatusChange: (status: StateStatusFilter) => void;
  /** Cities list: adds a state picker scoped to the selected country. */
  state?: string;
  onStateChange?: (iso: string) => void;
}

/** Country / (state) / status filters for the geography master lists. */
export default function StateListFilters({
  country, status, onCountryChange, onStatusChange, state = '', onStateChange,
}: Props) {
  const showState = !!onStateChange;
  const { data: countries = [], isLoading } = useQuery({
    queryKey: ['masters', 'states', 'filter-countries'],
    queryFn: getStateCountries,
    staleTime: 60_000,
  });
  const selected = countries.find((c) => c.code === country) || null;

  const { data: statesRaw, isLoading: statesLoading } = useQuery({
    queryKey: ['masters', 'states', 'country', country],
    queryFn: () => getMasters('states', { country, limit: 2000, page: 1 }),
    enabled: showState && !!country,
    staleTime: 60_000,
  });
  const stateOptions = useMemo<StateFilterOption[]>(
    () => ((statesRaw?.data ?? []) as Array<{ code?: string; name?: string; metadata?: Record<string, unknown> }>)
      .map((s) => {
        const code = String(s.code || '').toUpperCase();
        return {
          iso: String(s.metadata?.isoCode || code.replace(/^[A-Z]{2}-/, '')).toUpperCase(),
          code,
          name: String(s.name || ''),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    [statesRaw],
  );
  const selectedState = stateOptions.find((s) => s.iso === state) || null;
  const filtered = !!country || status !== 'all' || !!state;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.25}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 1.5 }}
    >
      <Autocomplete<StateCountrySummary>
        size="small"
        options={countries}
        loading={isLoading}
        value={selected}
        onChange={(_, value) => {
          onCountryChange(value?.code || '');
          onStateChange?.('');
        }}
        getOptionLabel={(o) => `${countryFlag(o.code)} ${o.name} (${o.code})`.trim()}
        isOptionEqualToValue={(o, v) => o.code === v.code}
        filterOptions={(options, s) => {
          const q = s.inputValue.trim().toLowerCase();
          if (!q) return options;
          return options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q);
        }}
        renderOption={(props, o) => (
          <li {...props} key={o.code}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
              <span>{countryFlag(o.code)}</span>
              <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
              {!showState && <Typography variant="caption" color="text.secondary">{o.total}</Typography>}
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField {...params} label="Country" placeholder="All countries" />
        )}
        sx={{ width: { xs: '100%', sm: 260 } }}
      />
      {showState && (
        <Autocomplete<StateFilterOption>
          size="small"
          options={stateOptions}
          loading={statesLoading}
          disabled={!country}
          value={selectedState}
          onChange={(_, value) => onStateChange?.(value?.iso || '')}
          getOptionLabel={(o) => `${o.name} (${o.code})`}
          isOptionEqualToValue={(o, v) => o.iso === v.iso}
          renderInput={(params) => (
            <TextField {...params} label="State / Province" placeholder={country ? 'All states' : 'Pick a country first'} />
          )}
          sx={{ width: { xs: '100%', sm: 260 } }}
        />
      )}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={status}
        onChange={(_, value: StateStatusFilter | null) => value && onStatusChange(value)}
        aria-label="Status filter"
      >
        <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
        <ToggleButton value="active" sx={{ textTransform: 'none', px: 1.5 }}>Active</ToggleButton>
        <ToggleButton value="inactive" sx={{ textTransform: 'none', px: 1.5 }}>Inactive</ToggleButton>
      </ToggleButtonGroup>
      {filtered && (
        <Button
          size="small"
          onClick={() => { onCountryChange(''); onStateChange?.(''); onStatusChange('all'); }}
          sx={{ textTransform: 'none' }}
        >
          Clear filters
        </Button>
      )}
      {selected && !showState && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' } }}>
          {selected.name}: {selected.active} active of {selected.total}
        </Typography>
      )}
    </Stack>
  );
}
